<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import WhatsappSessionsAPI from 'dashboard/api/whatsappSessions';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const store = useStore();
const { isAdmin } = useAdmin();

const sessions = ref([]);
const limitReached = ref(false);
const restartingId = ref(null);
const deletingId = ref(null);
const showDeletePopup = ref(false);
const selectedSession = ref({});
// Ids de inboxes já excluídas cujo `DeleteObjectJob` (assíncrono) ainda pode
// não ter rodado: sem isso, um ciclo do polling que caia antes do job
// terminar traria a sessão de volta à lista (o card "pisca de volta"). Mapeia
// id → instante da exclusão; o id sai no primeiro payload que já não o traz
// (job concluído) OU após o TTL — um job que falhou/travou não pode esconder
// pra sempre uma inbox ainda viva: o card volta com o botão pra nova tentativa.
const recentlyDeleted = new Map();
const RECENTLY_DELETED_TTL_MS = 60000;
// Só fica `true` até a primeira resposta (sucesso ou falha) chegar — os
// ciclos seguintes do polling não voltam a mostrar "Carregando…" por cima
// da lista já preenchida.
const loading = ref(true);
const loadError = ref(false);
let timer = null;

// Rótulo próprio por estado (eram só 3 baldes antes: qr/preparando/conectando
// dividiam "Aguardando leitura", escondendo que preparando/sem_proxy ainda
// não têm nada pra escanear e que falhou é uma pane de verdade).
const statusLabel = state => {
  if (state === 'conectado') return t('WHATSAPP_CONNECT.STATUS.CONNECTED');
  if (state === 'pausado') return t('WHATSAPP_CONNECT.STATUS.PAUSED');
  if (state === 'numero_diferente')
    return t('WHATSAPP_CONNECT.STATUS.NUMBER_MISMATCH');
  if (state === 'qr') return t('WHATSAPP_CONNECT.STATUS.WAITING');
  if (state === 'conectando') return t('WHATSAPP_CONNECT.STATUS.CONNECTING');
  if (state === 'falhou') return t('WHATSAPP_CONNECT.STATUS.FAILED');
  if (state === 'expirado') return t('WHATSAPP_CONNECT.STATUS.EXPIRED');
  if (state === 'preparando' || state === 'sem_proxy')
    return t('WHATSAPP_CONNECT.STATUS.PREPARING');
  return t('WHATSAPP_CONNECT.STATUS.DISCONNECTED');
};

const statusClass = state => {
  if (state === 'conectado') return 'bg-n-teal-9';
  if (state === 'pausado' || state === 'numero_diferente')
    return 'bg-n-amber-9';
  return 'bg-n-slate-9';
};

const fetchSessions = async () => {
  try {
    const { data } = await WhatsappSessionsAPI.get();
    const payloadIds = new Set(data.payload.map(s => s.inbox_id));
    // Remove um id quando o servidor já não o lista (job concluído) OU quando
    // passou do TTL — sem crescer sem limite nem esconder pra sempre uma inbox
    // que sobreviveu a um job que falhou.
    const now = Date.now();
    [...recentlyDeleted.keys()].forEach(id => {
      if (
        !payloadIds.has(id) ||
        now - recentlyDeleted.get(id) > RECENTLY_DELETED_TTL_MS
      ) {
        recentlyDeleted.delete(id);
      }
    });
    sessions.value = data.payload.filter(s => !recentlyDeleted.has(s.inbox_id));
    limitReached.value = data.meta.limit_reached;
    loadError.value = false;
  } catch (error) {
    // Mantém os dados anteriores na tela (se já havia alguma); o próximo
    // ciclo do polling tenta de novo. Só vira um aviso explícito quando não
    // há nenhum dado pra mostrar (senão o cliente lê "nenhum número
    // conectado" numa falha passageira de rede).
    loadError.value = true;
  } finally {
    loading.value = false;
  }
};

const goToConnect = () => router.push({ name: 'whatsapp_connect' });

const openChat = inboxId =>
  router.push({
    name: 'inbox_dashboard',
    params: { accountId: accountId.value, inbox_id: inboxId },
  });

const showQr = inboxId =>
  router.push({
    name: 'whatsapp_connect',
    params: { accountId: accountId.value },
    query: { inbox_id: inboxId },
  });

// Só existe QR de fato pra mostrar quando o estado é `qr`. Em `preparando`
// (caixa nova provisionando OU sessão degradada por instabilidade da
// WAHA/pool do daemon — o backend zera `connected_phone` nesses dois casos,
// então esse campo não serve pra distinguir um do outro) e em `conectando`
// (já foi escaneado) o botão seria promessa vazia. Restringir a `qr` evita
// oferecer "Ver QR" — e o Cancelar dentro dele — sobre um número que só
// parece novo por causa da degradação.
const isTrackable = session => session.state === 'qr';

// `falhou` é o caso do incidente (sessão caiu e não devolve QR sozinha);
// `pausado` é parada intencional que o cliente quer retomar. Nos demais
// estados o servidor recusaria o reinício de qualquer forma — a tela nem
// sugere.
const canRestart = session =>
  session.state === 'falhou' || session.state === 'pausado';

// `expirado`: não existe mais sessão nenhuma pra reiniciar (a WAHA já
// descartou), então o caminho é recomeçar do formulário — sem essa opção a
// caixa ficava sem nenhum botão útil, só "Abrir conversa" pra uma conversa
// que já não recebe nada.
const isExpired = session => session.state === 'expirado';

// Traduz a resposta do endpoint (200/409/429/503) num texto que um leigo
// entende, sem citar código HTTP. 429 é cooldown de verdade (reinício recente).
// `not_restartable` (409) é ambíguo: também dispara quando a sessão se
// recuperou sozinha entre o último refresh da tela e o clique — nesse caso
// "já tem uma reconexão em andamento" seria notícia boa com cara de erro.
// Por isso as duas usam um texto neutro: a tela se corrige no próximo ciclo
// de qualquer forma. `not_provisioned` e `session_gone` significam que não
// há mais sessão pra reiniciar. 503 é o daemon/WAHA fora do ar.
const restartErrorMessage = error => {
  const status = error?.response?.status;
  const code = error?.response?.data?.error;
  if (status === 429 || code === 'not_restartable') {
    return t('WHATSAPP_CONNECT.ERRORS.RESTART_STATE_CHANGED');
  }
  if (code === 'not_provisioned' || code === 'session_gone') {
    return t('WHATSAPP_CONNECT.ERRORS.RESTART_NEEDS_SETUP');
  }
  if (status === 503) {
    return t('WHATSAPP_CONNECT.ERRORS.RESTART_UNAVAILABLE');
  }
  return t('WHATSAPP_CONNECT.ERRORS.RESTART_FAILED');
};

const restart = async inboxId => {
  // Guarda contra clique duplo: a atribuição abaixo acontece antes de
  // qualquer await, então a segunda chamada (mesmo antes do próximo render)
  // já encontra `restartingId` preenchido e sai sem disparar outro reinício.
  if (restartingId.value) return;
  restartingId.value = inboxId;
  try {
    await WhatsappSessionsAPI.restart(inboxId);
    showQr(inboxId);
  } catch (error) {
    useAlert(restartErrorMessage(error));
  } finally {
    restartingId.value = null;
  }
};

// Exclusão de conexão: reusa a ação `inboxes/delete` (mesma já usada no
// Connect.vue). Ela destrói a inbox API → cascata destrói a linha
// `channel_api` → dispara o trigger `n8n_channel_api_delete` → o daemon
// encerra a sessão na WAHA e libera o proxy. Apaga também as conversas da
// caixa (é o delete padrão de inbox), por isso o modal exige digitar o nome.
const confirmDeleteMessage = computed(
  () =>
    `${t('WHATSAPP_CONNECT.DELETE.CONFIRM.MESSAGE')} ${selectedSession.value.name}?`
);
const deleteConfirmText = computed(
  () =>
    `${t('WHATSAPP_CONNECT.DELETE.CONFIRM.YES')} ${selectedSession.value.name}`
);
const deleteRejectText = computed(
  () =>
    `${t('WHATSAPP_CONNECT.DELETE.CONFIRM.NO')} ${selectedSession.value.name}`
);
const confirmPlaceHolderText = computed(() =>
  t('WHATSAPP_CONNECT.DELETE.CONFIRM.PLACE_HOLDER', {
    name: selectedSession.value.name,
  })
);

const openDelete = session => {
  selectedSession.value = session;
  showDeletePopup.value = true;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedSession.value = {};
};

const confirmDeletion = async () => {
  const inboxId = selectedSession.value.inbox_id;
  closeDelete();
  // Guarda contra clique duplo (mesma lógica do restart): a atribuição
  // acontece antes do await, então uma segunda chamada sai sem re-disparar.
  if (!inboxId || deletingId.value) return;
  deletingId.value = inboxId;
  try {
    await store.dispatch('inboxes/delete', inboxId);
    recentlyDeleted.set(inboxId, Date.now());
    sessions.value = sessions.value.filter(s => s.inbox_id !== inboxId);
    useAlert(t('WHATSAPP_CONNECT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('WHATSAPP_CONNECT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    deletingId.value = null;
  }
};

const canConnect = computed(() => !limitReached.value);

onMounted(() => {
  fetchSessions();
  timer = setInterval(fetchSessions, 10000);
});

onUnmounted(() => clearInterval(timer));
</script>

<template>
  <div class="w-full h-full p-8 overflow-auto">
    <div class="flex items-start justify-between max-w-3xl mb-6">
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ $t('WHATSAPP_CONNECT.TITLE') }}
        </h1>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_CONNECT.SUBTITLE') }}
        </p>
      </div>
      <NextButton
        solid
        teal
        :disabled="!canConnect"
        :label="$t('WHATSAPP_CONNECT.CONNECT_BUTTON')"
        @click="goToConnect"
      />
    </div>

    <!-- Informativo, sem cor de alerta: pra quem já usa seu único número essa
         é a situação normal, não um problema — "fale com o suporte" é
         exatamente o que a feature existe pra evitar. -->
    <p v-if="limitReached" class="max-w-3xl mb-4 text-sm text-n-slate-11">
      {{ $t('WHATSAPP_CONNECT.LIMIT_INFO') }}
    </p>

    <p v-if="loading" class="text-sm text-n-slate-11">
      {{ $t('WHATSAPP_CONNECT.LOADING') }}
    </p>
    <template v-else>
      <p v-if="loadError && !sessions.length" class="text-sm text-n-slate-11">
        {{ $t('WHATSAPP_CONNECT.LOAD_ERROR') }}
      </p>
      <p v-else-if="!sessions.length" class="text-sm text-n-slate-11">
        {{ $t('WHATSAPP_CONNECT.EMPTY') }}
      </p>

      <ul v-else class="max-w-3xl m-0 list-none">
        <li
          v-for="session in sessions"
          :key="session.inbox_id"
          class="flex items-center justify-between gap-4 px-4 py-3 mb-2 border rounded-lg border-n-weak bg-n-solid-2"
        >
          <div class="min-w-0">
            <p class="font-medium truncate text-n-slate-12">
              {{ session.name }}
            </p>
            <p class="text-sm text-n-slate-11">
              {{ session.connected_phone || session.phone_number || '—' }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <span class="flex items-center gap-2 text-sm text-n-slate-11">
              <span
                class="rounded-full size-2"
                :class="statusClass(session.state)"
              />
              {{ statusLabel(session.state) }}
            </span>
            <NextButton
              v-if="isTrackable(session)"
              outline
              teal
              :label="$t('WHATSAPP_CONNECT.ACTIONS.SHOW_QR')"
              @click="showQr(session.inbox_id)"
            />
            <NextButton
              v-if="canRestart(session)"
              outline
              teal
              :is-loading="restartingId === session.inbox_id"
              :disabled="!!restartingId"
              :label="$t('WHATSAPP_CONNECT.ACTIONS.RECONNECT')"
              @click="restart(session.inbox_id)"
            />
            <!-- `expirado`: não há mais sessão pra reiniciar, mas a caixa
                 ainda existe (ocupando a vaga do plano) — entrar em modo
                 retomada (em vez de formulário em branco) é o que dá à tela
                 de limite a mensagem certa e o botão de apagar essa caixa
                 órfã, com a confirmação de dois cliques preservada. -->
            <NextButton
              v-if="isExpired(session)"
              outline
              teal
              :label="$t('WHATSAPP_CONNECT.ACTIONS.RECONNECT')"
              @click="showQr(session.inbox_id)"
            />
            <NextButton
              outline
              slate
              :label="$t('WHATSAPP_CONNECT.OPEN_CHAT')"
              @click="openChat(session.inbox_id)"
            />
            <NextButton
              v-if="isAdmin"
              outline
              ruby
              :is-loading="deletingId === session.inbox_id"
              :disabled="!!deletingId"
              :label="$t('WHATSAPP_CONNECT.ACTIONS.DELETE')"
              @click="openDelete(session)"
            />
          </div>
        </li>
      </ul>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('WHATSAPP_CONNECT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedSession.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </div>
</template>
